import useScheduleRealtime from '../../realtime/useScheduleRealtime';
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import EditJobModal from "./EditJobModal";
import AccountingModal from "./AccountingModal";
import { formatDateLocalEnhanced } from "../../utils/dateUtils";
import { hasShippingPackDue } from '../../utils/shippingEligibility';
import { hasAnyEditPermission } from './utils/schedulePermissions';
import { fetchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import SortableTableHeader from "./SortableTableHeader";
import { getNextSortConfig, sortTableRows } from "./utils/tableSorting";
import CompactScheduleSection from "./components/CompactScheduleSection";

const TABLE_COLUMNS = [
  { key: "JobNumber", label: "Job Number", type: "text" },
  { key: "ProjectName", label: "Project Name", type: "text" },
  { key: "ModelNumber", label: "Model Number", type: "text" },
  { key: "OrderDate", label: "Order Date", type: "date" },
  { key: "ProductionRelease", label: "Release To Scheduling", type: "date" },
  { key: "RequestedDeliveryDate", label: "Requested Delivery Date", type: "date" },
  { key: "SalesRep", label: "Sales Rep", type: "text" },
  { key: "State", label: "State", type: "text" },
  { key: "JobType", label: "Job Type", type: "text" },
  { key: "PaymentTerms", label: "Payment Terms", type: "text" },
  { key: "DollarValue", label: "Dollar Value", type: "currency" },
  { key: "Notes", label: "Notes", type: "text" },
];

const OnHoldJobs = ({ filteredJobs = [], canEdit = true, readyToShipOnly = false, suppliedJobs }) => {
  const dispatch = useDispatch();
  // Use filteredJobs prop if available, otherwise fall back to Redux store
  const jobScheduleDataFromRedux = useSelector((state) => state.jobSchedule.data);
  const apiUrls = useSelector(selectApiUrls);
  const [onHoldApiJobs, setOnHoldApiJobs] = useState([]);
  const [hasLoadedApiJobs, setHasLoadedApiJobs] = useState(false);
  const [loadingApiJobs, setLoadingApiJobs] = useState(false);
  const [apiJobsError, setApiJobsError] = useState(null);
  
  // Get user roles for permission checking
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  const canEditAccess = Boolean(canEdit) && hasAnyEditPermission(userRoles);
  
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  const [accountingJob, setAccountingJob] = useState(null);
  const [visibleRows, setVisibleRows] = useState(50); // Initial number of rows to display
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const containerRef = useRef(null);

  useScheduleRealtime({ view: 'hold', enabled: suppliedJobs === undefined && !readyToShipOnly, blocked: loadingApiJobs || showModal || showAccountingModal, apply: rows => { setOnHoldApiJobs(rows); setHasLoadedApiJobs(true); setApiJobsError(null); } });

  useEffect(() => {
    if (suppliedJobs !== undefined) return undefined;
    let isMounted = true;

    const loadOnHoldJobs = async () => {
      if (!apiUrls?.fullSchedule) {
        return;
      }

      setLoadingApiJobs(true);
      setApiJobsError(null);

      try {
        const queryJoiner = apiUrls.fullSchedule.includes("?") ? "&" : "?";
        const url = `${apiUrls.fullSchedule}${queryJoiner}jobStatus=${encodeURIComponent(readyToShipOnly ? 'Hold/ Ready to Ship' : '*Hold*')}`;
        const response = await dispatch(fetchData({ url })).unwrap();
        const raw = response?.data || response || {};
        const dedupedJobs = Array.isArray(raw)
          ? raw
          : Object.values(raw)
              .map((item) => item?.GeneralProjectInfo)
              .filter(Boolean);

        if (isMounted) {
          setOnHoldApiJobs(dedupedJobs);
          setHasLoadedApiJobs(true);
        }
      } catch (error) {
        console.error('OnHoldJobs: Failed to load jobs from full schedule endpoint:', error);
        if (isMounted) {
          setApiJobsError('Failed to load on hold jobs.');
          setHasLoadedApiJobs(true);
        }
      } finally {
        if (isMounted) {
          setLoadingApiJobs(false);
        }
      }
    };

    loadOnHoldJobs();

    return () => {
      isMounted = false;
    };
  }, [dispatch, apiUrls?.fullSchedule, readyToShipOnly, suppliedJobs]);

  const handleRowDoubleClick = (job) => {
    if (!canEdit) return;
    // Check if user has accounting role and show accounting modal
    if (userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling) {
      setAccountingJob(job);
      setShowAccountingModal(true);
      return;
    }
    
    // Only allow double-click functionality if user has edit permissions
    if (canEditAccess) {
      setSelectedJob(job);
      setShowModal(true);
    }
    // Do nothing if the user doesn't have permissions
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setVisibleRows((prev) => prev + 50); // Load 50 more rows
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);  // Use filteredJobs if provided (from global search), otherwise filter locally
  // Match the pattern used in working components like canceledJobs and ShippedJobs
  const dataToUse = suppliedJobs !== undefined ? suppliedJobs : filteredJobs.length > 0
    ? filteredJobs
    : hasLoadedApiJobs
      ? onHoldApiJobs
      : (Array.isArray(jobScheduleDataFromRedux) ? jobScheduleDataFromRedux : []);
    
  // Debug logging
  console.log('OnHoldJobs - dataToUse length:', dataToUse.length);
  console.log('OnHoldJobs - filteredJobs:', filteredJobs);
  console.log('OnHoldJobs - jobScheduleDataFromRedux length:', jobScheduleDataFromRedux?.length);
    // Log first few jobs to see their JobStatus values
  if (dataToUse.length > 0) {
    console.log('OnHoldJobs - First 5 jobs with JobStatus:', 
      dataToUse.slice(0, 5).map(job => ({ 
        JobNumber: job.JobNumber, 
        JobStatus: job.JobStatus,
        ActualShipDate: job.ActualShipDate 
      }))
    );
    
    // Log unique JobStatus values to see what's available
    const uniqueStatuses = [...new Set(dataToUse.map(job => job.JobStatus))].filter(Boolean);
    console.log('OnHoldJobs - All unique JobStatus values:', uniqueStatuses);
  }

    // Filter for "Hold/ Ready to Ship" jobs
  const holdReadyToShipJobs = dataToUse.filter(
    (job) => {
      const jobStatus = job.JobStatus?.trim();
      return jobStatus === "Hold/ Ready to Ship" && (!readyToShipOnly || hasShippingPackDue(job));
    }
  );
  
  // Filter for "On Hold" jobs - be more comprehensive with status matching
  const onHoldJobs = dataToUse.filter(
    (job) => {
      const jobStatus = job.JobStatus?.trim();
      return !readyToShipOnly && (jobStatus === "On Hold" || jobStatus === "Hold");
    }
  );

  
  // Debug logging for filtered results
  console.log('OnHoldJobs - onHoldJobs length:', onHoldJobs.length);
  console.log('OnHoldJobs - holdReadyToShipJobs length:', holdReadyToShipJobs.length);
  // We'll keep them separate for display with headers
  const sortedOnHoldJobs = useMemo(
    () => sortTableRows(onHoldJobs, TABLE_COLUMNS, sortConfig),
    [onHoldJobs, sortConfig]
  );
  const sortedReadyToShipJobs = useMemo(
    () => sortTableRows(holdReadyToShipJobs, TABLE_COLUMNS, sortConfig),
    [holdReadyToShipJobs, sortConfig]
  );
  const visibleOnHoldJobs = sortedOnHoldJobs.slice(0, visibleRows);
  const visibleReadyToShipJobs = sortedReadyToShipJobs.slice(0, visibleRows);

  const handleSort = (key) => {
    setSortConfig((currentSort) => getNextSortConfig(currentSort, key));
    setVisibleRows(50);
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };
  
  // Calculate total length for scroll loading
  const totalJobsCount = onHoldJobs.length + holdReadyToShipJobs.length;
    // Render a row with the section header
  const renderSectionHeader = (title, count) => (
    <tr className="section-header schedule-section-row" style={{ backgroundColor: "#e9ecef" }}>
      <td colSpan="12">
        <strong>{title}</strong> ({count} job{count !== 1 ? 's' : ''})
      </td>
    </tr>
  );

  // Format currency
  const formatCurrency = (value) => {
    const numValue = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  const compactFields = [
    { key: "ProjectName", label: "Project Name", className: "compact-inline-project-column" },
    { key: "ModelNumber", label: "Model Number", className: "compact-inline-model-column" },
    { key: "OrderDate", label: "Order Date", render: (job) => formatDateLocalEnhanced(job.OrderDate) },
    { key: "ProductionRelease", label: "Production Release", render: (job) => formatDateLocalEnhanced(job.ProductionRelease) },
    { key: "SalesRep", label: "Sales Rep" },
    { key: "State", label: "State" },
    { key: "JobType", label: "Job Type" },
    { key: "PaymentTerms", label: "Payment Terms" },
    { key: "DollarValue", label: "Dollar Value", render: (job) => formatCurrency(job.DollarValue) },
    { key: "Notes", label: "Notes", className: "notes-column" },
  ];

  return (
    <div
      className="on-hold-jobs-container"
      style={{ overflow: "auto", maxHeight: "80vh" }}
      ref={containerRef}
    >
      {loadingApiJobs && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading on hold jobs...</span>
          </Spinner>
          <p>Loading on hold jobs...</p>
        </div>
      )}

      {apiJobsError && (onHoldJobs.length + holdReadyToShipJobs.length) === 0 && (
        <Alert variant="danger">{apiJobsError}</Alert>
      )}

      <div className="schedule-simple-desktop-table">
        <Table striped bordered hover responsive className="schedule-responsive-table">
        <SortableTableHeader
          columns={TABLE_COLUMNS}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        <tbody>
          {(visibleOnHoldJobs.length > 0 || visibleReadyToShipJobs.length > 0) ? (
            <>
              {/* Hold/ Ready to Ship Jobs Section */}
              {visibleReadyToShipJobs.length > 0 && renderSectionHeader("Hold/ Ready to Ship", holdReadyToShipJobs.length)}              {visibleReadyToShipJobs.map((job) => (
                <tr
                  key={job.ID}
                  className="schedule-collapsible-row"
                  onDoubleClick={() => handleRowDoubleClick(job)}
                  style={{ cursor: canEditAccess ? 'pointer' : 'default' }}
                  title={canEditAccess ? "Double-click to edit" : ""}
                >
                  <td data-label="Job Number" className="schedule-card-primary">{job.JobNumber}</td>
                  <td data-label="Project Name" className="schedule-card-primary">{job.ProjectName}</td>
                  <td data-label="Model Number" className="schedule-card-primary">{job.ModelNumber}</td>
                  <td data-label="Order Date" className="schedule-card-secondary">{job.OrderDate ? new Date(job.OrderDate).toLocaleDateString() : ""}</td>
                  <td data-label="Production Release" className="schedule-card-secondary">{job.ProductionRelease ? new Date(job.ProductionRelease).toLocaleDateString() : ""}</td>
                  <td data-label="Requested Delivery" className="schedule-card-secondary">{job.RequestedDeliveryDate ? new Date(job.RequestedDeliveryDate).toLocaleDateString() : ""}</td>
                  <td data-label="Sales Rep" className="schedule-card-secondary">{job.SalesRep}</td>
                  <td data-label="State" className="schedule-card-secondary">{job.State}</td>
                  <td data-label="Job Type" className="schedule-card-primary">{job.JobType}</td>
                  <td data-label="Payment Terms" className="schedule-card-secondary">{job.PaymentTerms}</td>
                  <td data-label="Dollar Value" className="schedule-card-secondary" style={{ textAlign: 'right' }}>{formatCurrency(job.DollarValue)}</td>
                  <td data-label="Notes" className="schedule-card-secondary">{job.Notes}</td>
                  {(canEditAccess || (canEdit && userRolesMapped && userRolesMapped.Accounting)) && (
                    <td data-label="Actions" className="schedule-card-action-cell">
                      <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleRowDoubleClick(job)}>
                        Open job
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {/* On Hold Jobs Section */}
              {visibleOnHoldJobs.length > 0 && renderSectionHeader("On Hold", onHoldJobs.length)}              {visibleOnHoldJobs.map((job) => (
                <tr
                  key={job.ID}
                  className="schedule-collapsible-row"
                  onDoubleClick={() => handleRowDoubleClick(job)}
                  style={{ cursor: canEditAccess ? 'pointer' : 'default' }}
                  title={canEditAccess ? "Double-click to edit" : ""}
                >
                  <td data-label="Job Number" className="schedule-card-primary">{job.JobNumber}</td>
                  <td data-label="Project Name" className="schedule-card-primary">{job.ProjectName}</td>
                  <td data-label="Model Number" className="schedule-card-primary">{job.ModelNumber}</td>
                  <td data-label="Order Date" className="schedule-card-secondary">{job.OrderDate ? new Date(job.OrderDate).toLocaleDateString() : ""}</td>
                  <td data-label="Production Release" className="schedule-card-secondary">{job.ProductionRelease ? new Date(job.ProductionRelease).toLocaleDateString() : ""}</td>
                  <td data-label="Requested Delivery" className="schedule-card-secondary">{job.RequestedDeliveryDate ? new Date(job.RequestedDeliveryDate).toLocaleDateString() : ""}</td>
                  <td data-label="Sales Rep" className="schedule-card-secondary">{job.SalesRep}</td>
                  <td data-label="State" className="schedule-card-secondary">{job.State}</td>
                  <td data-label="Job Type" className="schedule-card-primary">{job.JobType}</td>
                  <td data-label="Payment Terms" className="schedule-card-secondary">{job.PaymentTerms}</td>
                  <td data-label="Dollar Value" className="schedule-card-secondary" style={{ textAlign: 'right' }}>{formatCurrency(job.DollarValue)}</td>
                  <td data-label="Notes" className="schedule-card-secondary">{job.Notes}</td>
                  {(canEditAccess || (canEdit && userRolesMapped && userRolesMapped.Accounting)) && (
                    <td data-label="Actions" className="schedule-card-action-cell">
                      <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleRowDoubleClick(job)}>
                        Open job
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              
            </>          ) : (
            <tr>
              <td colSpan="12" className="schedule-empty-row">No data available</td>
            </tr>
          )}
        </tbody>
        </Table>
      </div>

      <div className="schedule-simple-compact-list">
        {visibleReadyToShipJobs.length > 0 && (
          <CompactScheduleSection
            title={`Hold/ Ready to Ship (${holdReadyToShipJobs.length})`}
            jobs={visibleReadyToShipJobs}
            dateLabel="Requested Delivery"
            getDateValue={(job) => formatDateLocalEnhanced(job.RequestedDeliveryDate)}
            fields={compactFields}
          />
        )}
        {visibleOnHoldJobs.length > 0 && (
          <CompactScheduleSection
            title={`On Hold (${onHoldJobs.length})`}
            jobs={visibleOnHoldJobs}
            dateLabel="Requested Delivery"
            getDateValue={(job) => formatDateLocalEnhanced(job.RequestedDeliveryDate)}
            fields={compactFields}
          />
        )}
        {visibleOnHoldJobs.length === 0 && visibleReadyToShipJobs.length === 0 && (
          <div className="schedule-empty-row">No data available</div>
        )}
      </div>
      {visibleRows < totalJobsCount && (
        <div style={{ textAlign: "center", padding: "10px" }}>
          Loading more rows...
        </div>
      )}      <Modal className="job-schedule-modal" show={showModal} onHide={handleCloseModal} size="xl">
        <EditJobModal
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          handleCloseModal={handleCloseModal}
          handleSaveChanges={() => {}}
          userRoles={userRoles}
        />
      </Modal>
      
      {/* Accounting Modal for accounting users */}
      <AccountingModal
        show={showAccountingModal}
        onHide={() => setShowAccountingModal(false)}
        job={accountingJob}
      />
    </div>
  );
};

export default OnHoldJobs;
